import { useEffect, useState, useCallback } from "react";
import { Sparkles, Plus, Trash2, Loader2, Check, X, Layers, Wand2, Upload, Image as ImageIcon, Pencil, Save, RotateCcw } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionCard, StatCard } from "@/components/ui-helpers";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const imgSrc = (u) => (u && u.startsWith("/api") ? BACKEND + u : u);

const STATUS = {
  draft: "bg-slate-100 text-slate-600", pending: "bg-amber-100 text-amber-700",
  published: "bg-emerald-100 text-emerald-700", rejected: "bg-rose-100 text-rose-700",
};

const PRICING = [["fixed", "Fixed"], ["starting_from", "Starting from"], ["hourly", "Per hour"], ["per_visit", "Per visit"],
  ["per_session", "Per session"], ["per_appliance", "Per appliance"], ["per_room", "Per room"], ["package", "Package"], ["custom_quote", "Custom quote"]];
const BOOKING = [["fixed", "Instant booking"], ["appointment", "Appointment"], ["provider_approval", "Provider approval"], ["quote", "Quote required"], ["request", "Request service"]];
const PAYMENT = [["pay_after", "Pay after service"], ["pay_now", "Pay online now"], ["deposit", "Deposit + balance"]];

function CategoriesTab() {
  const [cats, setCats] = useState(null);
  const [name, setName] = useState("");
  const [parent, setParent] = useState("");
  const [imgBusy, setImgBusy] = useState("");
  const [bulk, setBulk] = useState(null);
  const load = useCallback(() => { api.get("/admin/service-categories/manage").then((r) => setCats(r.data.categories || [])).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  useEffect(() => { load(); }, [load]);

  const add = async () => { if (!name.trim()) return; try { await api.post("/admin/service-categories", { name, parent_id: parent || null, sort: (cats?.length || 0) }); toast.success("Category added"); setName(""); setParent(""); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggle = async (c) => { try { await api.patch(`/admin/service-categories/${c.service_category_id}`, { active: !c.active }); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const toggleVisible = async (c) => { try { await api.patch(`/admin/service-categories/${c.service_category_id}`, { visible: c.visible === false }); setCats((prev) => prev.map((x) => x.service_category_id === c.service_category_id ? { ...x, visible: c.visible === false } : x)); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const del = async (c) => { if (!window.confirm(`Delete "${c.name}"?`)) return; try { await api.delete(`/admin/service-categories/${c.service_category_id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const genImg = async (c) => { setImgBusy(c.service_category_id); try { const r = await api.post(`/admin/service-categories/${c.service_category_id}/ai-image`); toast.success("Tile image generated"); setCats((prev) => prev.map((x) => x.service_category_id === c.service_category_id ? { ...x, image: r.data.image } : x)); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setImgBusy(""); } };
  const uploadImg = (c, file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => { setImgBusy(c.service_category_id); try { const r = await api.post(`/admin/service-categories/${c.service_category_id}/image`, { content_b64: reader.result }); toast.success("Tile image updated"); setCats((prev) => prev.map((x) => x.service_category_id === c.service_category_id ? { ...x, image: r.data.image } : x)); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setImgBusy(""); } };
    reader.readAsDataURL(file);
  };
  const clearImg = async (c) => { setImgBusy(c.service_category_id); try { await api.post(`/admin/service-categories/${c.service_category_id}/image`, { content_b64: "" }); setCats((prev) => prev.map((x) => x.service_category_id === c.service_category_id ? { ...x, image: "" } : x)); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setImgBusy(""); } };
  const regenAll = async () => {
    const tops = (cats || []).filter((c) => !c.parent_id);
    if (!tops.length) return;
    if (!window.confirm(`Generate a fresh face-safe AI banner for all ${tops.length} homepage categories? This can take a minute or two.`)) return;
    setBulk({ done: 0, total: tops.length });
    let ok = 0;
    for (let i = 0; i < tops.length; i++) {
      const c = tops[i];
      setImgBusy(c.service_category_id);
      try { const r = await api.post(`/admin/service-categories/${c.service_category_id}/ai-image`); setCats((prev) => prev.map((x) => x.service_category_id === c.service_category_id ? { ...x, image: r.data.image } : x)); ok++; }
      catch { /* skip individual failures, keep going */ }
      setImgBusy(""); setBulk({ done: i + 1, total: tops.length });
    }
    setBulk(null);
    toast.success(`Regenerated ${ok} of ${tops.length} homepage tiles`);
  };

  if (cats === null) return <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const nameOf = (id) => cats.find((x) => x.service_category_id === id)?.name;

  return (
    <SectionCard title="Service categories" testid="service-categories">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-xl text-xs text-slate-500">Fully Super-Admin controlled. Add categories/subcategories, activate/deactivate, toggle each top-level category on/off the homepage grid, and give it a tile image (AI-generate or upload).</p>
        <button onClick={regenAll} disabled={!!bulk || !!imgBusy} data-testid="regen-all-tiles" className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-fuchsia-600 px-4 py-2 text-sm font-semibold text-white hover:bg-fuchsia-700 disabled:opacity-50">
          {bulk ? <><Loader2 className="h-4 w-4 animate-spin" /> Regenerating {bulk.done}/{bulk.total}…</> : <><Wand2 className="h-4 w-4" /> Regenerate all homepage tiles</>}
        </button>
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-2" data-testid="add-category-row">
        <label className="text-xs font-semibold text-slate-500">New category<Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Plumbing" className="mt-1 w-52" data-testid="new-category-name" /></label>
        <label className="text-xs font-semibold text-slate-500">Parent (optional)
          <select value={parent} onChange={(e) => setParent(e.target.value)} data-testid="new-category-parent" className="mt-1 h-9 w-52 rounded-md border border-slate-200 px-2 text-sm">
            <option value="">— Top level —</option>
            {cats.filter((c) => !c.parent_id).map((c) => <option key={c.service_category_id} value={c.service_category_id}>{c.name}</option>)}
          </select>
        </label>
        <button onClick={add} data-testid="add-category-btn" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> Add</button>
      </div>
      <div className="divide-y divide-slate-50 rounded-xl border border-slate-100">
        {cats.map((c) => (
          <div key={c.service_category_id} className="flex flex-wrap items-center justify-between gap-2 p-3" data-testid={`category-row-${c.service_category_id}`}>
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative h-14 w-20 shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50" data-testid={`category-tile-${c.service_category_id}`}>
                {c.image ? <img src={imgSrc(c.image)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-slate-300"><ImageIcon className="h-5 w-5" /></div>}
                {imgBusy === c.service_category_id && <div className="absolute inset-0 flex items-center justify-center bg-white/70"><Loader2 className="h-4 w-4 animate-spin text-fuchsia-600" /></div>}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800">{c.parent_id ? <span className="text-slate-400">{nameOf(c.parent_id)} › </span> : null}{c.name}</p>
                <p className="text-[11px] text-slate-400">{c.service_count || 0} services · /{c.slug}</p>
                {(
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <button onClick={() => toggleVisible(c)} data-testid={`cat-tile-toggle-${c.service_category_id}`} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${c.visible === false ? "bg-slate-100 text-slate-500" : "bg-blue-100 text-blue-700"}`}>{c.visible === false ? "Off homepage" : "On homepage"}</button>
                    <button onClick={() => genImg(c)} disabled={imgBusy} data-testid={`cat-ai-image-${c.service_category_id}`} className="inline-flex items-center gap-1 rounded-full bg-fuchsia-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-fuchsia-700 disabled:opacity-50"><Wand2 className="h-3 w-3" /> AI tile</button>
                    <label data-testid={`cat-upload-label-${c.service_category_id}`} className="inline-flex cursor-pointer items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"><Upload className="h-3 w-3" /> Upload<input type="file" accept="image/*" className="hidden" data-testid={`cat-upload-${c.service_category_id}`} onChange={(e) => { uploadImg(c, e.target.files?.[0]); e.target.value = ""; }} /></label>
                    {c.image && <button onClick={() => clearImg(c)} disabled={imgBusy} data-testid={`cat-clear-image-${c.service_category_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-500 hover:border-rose-200 hover:text-rose-500">Clear</button>}
                  </div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => toggle(c)} data-testid={`category-toggle-${c.service_category_id}`} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${c.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.active ? "Active" : "Hidden"}</button>
              <button onClick={() => del(c)} data-testid={`category-delete-${c.service_category_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:border-rose-200 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function EditModal({ service, categories, onClose, onSaved }) {
  const [f, setF] = useState({ ...service });
  const [busy, setBusy] = useState(false);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const save = async () => {
    if (!(f.name || "").trim()) { toast.error("Name is required."); return; }
    setBusy(true);
    try {
      const payload = { ...f, price: Number(f.price) || 0, duration_minutes: Number(f.duration_minutes) || 0 };
      await api.put(`/admin/services/${service.service_id}`, payload);
      toast.success("Service updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="admin-service-edit-modal" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-5" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-900">Edit service</h3>
          <button onClick={onClose} data-testid="admin-svc-edit-close" className="rounded-lg p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-3">
          <label className="block text-xs font-semibold text-slate-500">Name<Input value={f.name || ""} onChange={(e) => set({ name: e.target.value })} className="mt-1" data-testid="admin-svc-name" /></label>
          <label className="block text-xs font-semibold text-slate-500">Category
            <select value={f.service_category_id || ""} onChange={(e) => set({ service_category_id: e.target.value })} data-testid="admin-svc-category" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm">
              <option value="">Select category…</option>
              {categories.map((c) => <option key={c.service_category_id} value={c.service_category_id}>{c.name}</option>)}
            </select>
          </label>
          <label className="block text-xs font-semibold text-slate-500">Description<textarea value={f.description || ""} onChange={(e) => set({ description: e.target.value })} data-testid="admin-svc-description" className="mt-1 h-20 w-full rounded-md border border-slate-200 p-2 text-sm" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs font-semibold text-slate-500">Pricing model
              <select value={f.pricing_model || "fixed"} onChange={(e) => set({ pricing_model: e.target.value })} data-testid="admin-svc-pricing" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm">{PRICING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </label>
            <label className="block text-xs font-semibold text-slate-500">Price (₹)<Input type="number" value={f.price ?? 0} onChange={(e) => set({ price: e.target.value })} className="mt-1" data-testid="admin-svc-price" /></label>
            <label className="block text-xs font-semibold text-slate-500">Booking model
              <select value={f.booking_model || "fixed"} onChange={(e) => set({ booking_model: e.target.value })} data-testid="admin-svc-booking" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm">{BOOKING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </label>
            <label className="block text-xs font-semibold text-slate-500">Payment timing
              <select value={f.payment_timing || "pay_after"} onChange={(e) => set({ payment_timing: e.target.value })} data-testid="admin-svc-payment" className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm">{PAYMENT.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
            </label>
            <label className="block text-xs font-semibold text-slate-500">Duration (mins)<Input type="number" value={f.duration_minutes ?? 0} onChange={(e) => set({ duration_minutes: e.target.value })} className="mt-1" data-testid="admin-svc-duration" /></label>
          </div>
          <p className="text-[11px] text-slate-400">Editing here does not reset the listing's approval status.</p>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="admin-svc-save" className="inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save changes</button>
        </div>
      </div>
    </div>
  );
}

function ListingsTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get(`/admin/services?status=${filter}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/marketplace/service-categories").then((r) => setCategories(r.data.categories || [])).catch(() => {}); }, []);

  const decide = async (s, action) => {
    let reason = "";
    if (action === "reject") { reason = window.prompt("Reason for rejection (shown to seller):") || ""; if (!reason) return; }
    try { await api.post(`/admin/services/${s.service_id}/decision`, { action, reason }); toast.success(action === "approve" ? "Published" : "Rejected"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (data === null) return <div className="flex h-40 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <SectionCard title="Service listings" testid="service-listings">
      <div className="mb-3 flex flex-wrap gap-2">
        {["pending", "published", "all"].map((k) => <button key={k} onClick={() => setFilter(k)} data-testid={`listings-filter-${k}`} className={`rounded-full px-3 py-1.5 text-xs font-medium ${filter === k ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>{k[0].toUpperCase() + k.slice(1)}</button>)}
      </div>
      <div className="divide-y divide-slate-50 rounded-xl border border-slate-100">
        {data.services.length === 0 && <p className="p-8 text-center text-sm text-slate-400" data-testid="listings-empty">No services here.</p>}
        {data.services.map((s) => (
          <div key={s.service_id} className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`listing-row-${s.service_id}`}>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-slate-800">{s.name}</p>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS[s.status] || STATUS.draft}`}>{s.status}</span>
              </div>
              <p className="mt-1 text-xs text-slate-400">{s.seller?.name || s.seller?.email || "—"} · {s.pricing_model === "custom_quote" ? "Custom quote" : `₹${s.price}`} · {s.booking_model}</p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {s.status === "pending" && (
                <>
                  <button onClick={() => decide(s, "approve")} data-testid={`listing-approve-${s.service_id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"><Check className="h-3.5 w-3.5" /> Approve</button>
                  <button onClick={() => decide(s, "reject")} data-testid={`listing-reject-${s.service_id}`} className="inline-flex items-center gap-1 rounded-full border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50"><X className="h-3.5 w-3.5" /> Reject</button>
                </>
              )}
              <button onClick={() => setEditing(s)} data-testid={`listing-edit-${s.service_id}`} className="inline-flex items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Pencil className="h-3.5 w-3.5" /> Edit</button>
            </div>
          </div>
        ))}
      </div>
      {editing && <EditModal service={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </SectionCard>
  );
}

function DedupeTab() {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [travelSel, setTravelSel] = useState({});
  const [dupSel, setDupSel] = useState({});
  const [undo, setUndo] = useState(null);
  const [digest, setDigest] = useState(false);
  const [history, setHistory] = useState([]);

  const loadUndo = useCallback(() => { api.get("/admin/service-categories/last-merge").then((r) => setUndo(r.data.last)).catch(() => {}); }, []);
  const loadDigest = useCallback(() => { api.get("/admin/service-categories/dupe-digest").then((r) => setDigest(!!r.data.enabled)).catch(() => {}); }, []);
  const loadHistory = useCallback(() => { api.get("/admin/service-categories/merge-history").then((r) => setHistory(r.data.history || [])).catch(() => {}); }, []);
  const toggleDigest = async () => { try { const r = await api.post("/admin/service-categories/dupe-digest", { enabled: !digest }); setDigest(r.data.enabled); toast.success(r.data.enabled ? "Weekly reminder emails ON" : "Weekly reminder emails OFF"); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const load = useCallback(() => {
    setAudit(null);
    api.get("/admin/service-categories/dedupe-audit").then((r) => {
      const a = r.data;
      const ts = {};
      (a.travel_groups || []).forEach((g) => { ts[g.category.id] = { include: false, target: g.fold_into.id }; });
      const ds = {};
      [...(a.duplicate_groups || []), ...(a.sibling_groups || [])].forEach((g) => {
        const surv = (g.members.find((m) => m.is_proposed_survivor) || g.members[0]).id;
        const remove = {};
        g.members.forEach((m) => { if (m.id !== surv) remove[m.id] = true; });
        ds[g.group_id] = { survivor: surv, remove };
      });
      setTravelSel(ts); setDupSel(ds); setAudit(a);
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    loadUndo(); loadDigest(); loadHistory();
  }, [loadUndo]);
  useEffect(() => { load(); }, [load]);

  const buildOps = () => {
    const ops = [];
    // travel folds — group included categories by chosen target
    const byTarget = {};
    (audit.travel_groups || []).forEach((g) => {
      const sel = travelSel[g.category.id];
      if (sel?.include) { (byTarget[sel.target] = byTarget[sel.target] || []).push(g.category.id); }
    });
    Object.entries(byTarget).forEach(([survivor_id, remove_ids]) => ops.push({ survivor_id, remove_ids }));
    // near-duplicate + sibling merges
    [...(audit.duplicate_groups || []), ...(audit.sibling_groups || [])].forEach((g) => {
      const sel = dupSel[g.group_id];
      if (!sel) return;
      const remove_ids = g.members.filter((m) => m.id !== sel.survivor && sel.remove[m.id]).map((m) => m.id);
      if (remove_ids.length) ops.push({ survivor_id: sel.survivor, remove_ids });
    });
    return ops;
  };

  const apply = async () => {
    const ops = buildOps();
    if (!ops.length) { toast.error("Nothing selected to merge."); return; }
    const totalRemove = ops.reduce((n, o) => n + o.remove_ids.length, 0);
    if (!window.confirm(`Merge ${totalRemove} categor${totalRemove === 1 ? "y" : "ies"} into their survivors? Listings and subcategories move first, then the duplicate is deleted.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/service-categories/dedupe-apply", { ops });
      const d = r.data;
      toast.success(`Merged ${d.categories_deleted} · moved ${d.listings_moved} listing(s), ${d.children_moved} subcategor(y/ies)`);
      if (d.errors?.length) toast.error(d.errors.join("; "));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doUndo = async () => {
    if (!window.confirm(`Undo the last merge? This restores ${undo.restores?.length || 0} categor${(undo.restores?.length || 0) === 1 ? "y" : "ies"} and moves their listings/subcategories back.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/service-categories/undo-merge");
      if (r.data.ok) toast.success(`Restored ${r.data.categories_restored} categor(y/ies), ${r.data.listings_restored} listing(s), ${r.data.children_restored} subcategor(y/ies)`);
      else toast.error(r.data.error || "Nothing to undo.");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!audit) return <div className="flex h-40 items-center justify-center text-slate-400" data-testid="dedupe-loading"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const nothing = (audit.travel_groups?.length || 0) === 0 && (audit.duplicate_groups?.length || 0) === 0 && (audit.sibling_groups?.length || 0) === 0;
  const pill = (n) => <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500">{n} listing{n === 1 ? "" : "s"}</span>;

  const renderGroup = (g) => {
    const sel = dupSel[g.group_id] || {};
    return (
      <div key={g.group_id} className="rounded-xl border border-slate-100 p-3" data-testid={`dedupe-dup-group-${g.group_id}`}>
        <p className="mb-2 text-xs text-slate-500">{g.reason}</p>
        <div className="space-y-1.5">
          {g.members.map((m) => {
            const isSurv = sel.survivor === m.id;
            return (
              <div key={m.id} className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50/60 px-2 py-1.5" data-testid={`dedupe-member-${m.id}`}>
                <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
                  <input type="radio" name={`surv-${g.group_id}`} checked={isSurv} onChange={() => setDupSel((p) => ({ ...p, [g.group_id]: { survivor: m.id, remove: Object.fromEntries(g.members.filter((x) => x.id !== m.id).map((x) => [x.id, true])) } }))} data-testid={`dedupe-survivor-${m.id}`} className="h-3.5 w-3.5 accent-emerald-600" />
                  Keep
                </label>
                <span className={`text-sm ${isSurv ? "font-bold text-emerald-700" : "font-semibold text-slate-800"}`}>{m.name}</span>
                {pill(m.listings)}
                {m.children > 0 && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-600">{m.children} sub</span>}
                {isSurv ? <span className="ml-auto text-[11px] font-semibold text-emerald-600">survivor</span> : (
                  <label className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-500">
                    <input type="checkbox" checked={!!sel.remove?.[m.id]} onChange={(e) => setDupSel((p) => ({ ...p, [g.group_id]: { ...sel, remove: { ...sel.remove, [m.id]: e.target.checked } } }))} data-testid={`dedupe-remove-${m.id}`} className="h-3.5 w-3.5 accent-rose-500" />
                    merge in
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <SectionCard title="Category clean-up" testid="dedupe-tab">
      <p className="mb-4 max-w-2xl text-xs text-slate-500">Step 1 — review. Nothing changes until you press <b>Apply selected merges</b>. Travel/transport categories fold into the Travel section; other look-alike categories merge onto one survivor. Listings and subcategories always move first, so nothing is lost.</p>

      {undo && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="dedupe-undo-banner">
          <RotateCcw className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="text-xs text-amber-800">Last merge restored <b>{(undo.restores || []).join(", ")}</b> can still be reversed.</span>
          <button onClick={doUndo} disabled={busy} data-testid="dedupe-undo-btn" className="ml-auto rounded-full bg-amber-600 px-3.5 py-1.5 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50">Undo last merge</button>
        </div>
      )}

      {nothing && <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4 text-sm text-emerald-700" data-testid="dedupe-clean">No duplicates found — the category tree looks clean. ✨</div>}


      {(audit.travel_groups?.length || 0) > 0 && (
        <div className="mb-6" data-testid="dedupe-travel-section">
          <h3 className="mb-2 text-sm font-bold text-slate-800">Travel / transport outside the Travel section</h3>
          <div className="divide-y divide-slate-100 rounded-xl border border-slate-100">
            {audit.travel_groups.map((g) => {
              const sel = travelSel[g.category.id] || {};
              const targets = [{ id: audit.travel_root_id, name: "Travel (top level)" }, ...(audit.travel_subcats || []).map((s) => ({ id: s.id, name: `Travel › ${s.name}` }))];
              return (
                <div key={g.category.id} className="flex flex-wrap items-center gap-3 p-3" data-testid={`dedupe-travel-row-${g.category.id}`}>
                  <input type="checkbox" checked={!!sel.include} onChange={(e) => setTravelSel((p) => ({ ...p, [g.category.id]: { ...sel, include: e.target.checked } }))} data-testid={`dedupe-travel-check-${g.category.id}`} className="h-4 w-4 accent-fuchsia-600" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2"><span className="text-sm font-semibold text-slate-800">{g.category.name}</span>{pill(g.category.listings)}<span className="text-[11px] text-slate-400">under {g.category.parent_name || "root"}</span></div>
                    <p className="text-xs text-slate-500">{g.reason}</p>
                  </div>
                  <label className="text-xs text-slate-500">Fold into
                    <select value={sel.target} onChange={(e) => setTravelSel((p) => ({ ...p, [g.category.id]: { ...sel, target: e.target.value } }))} data-testid={`dedupe-travel-target-${g.category.id}`} className="ml-1 h-8 rounded-md border border-slate-200 px-2 text-sm">
                      {targets.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {(audit.duplicate_groups?.length || 0) > 0 && (
        <div className="mb-6" data-testid="dedupe-dup-section">
          <h3 className="mb-2 text-sm font-bold text-slate-800">Near-duplicate root categories</h3>
          <div className="space-y-3">
            {audit.duplicate_groups.map(renderGroup)}
          </div>
        </div>
      )}

      {(audit.sibling_groups?.length || 0) > 0 && (
        <div className="mb-6" data-testid="dedupe-sibling-section">
          <h3 className="mb-2 text-sm font-bold text-slate-800">Look-alike subcategories (same parent)</h3>
          <div className="space-y-3">
            {audit.sibling_groups.map(renderGroup)}
          </div>
        </div>
      )}

      {!nothing && (
        <div className="flex items-center gap-3">
          <button onClick={apply} disabled={busy} data-testid="dedupe-apply-btn" className="inline-flex items-center gap-2 rounded-full brand-gradient-bg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Apply selected merges</button>
          <button onClick={load} disabled={busy} data-testid="dedupe-refresh-btn" className="rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Re-scan</button>
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid="dupe-digest-row">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-800">Weekly reminder email to admins</p>
          <p className="text-xs text-slate-500">A Monday email while duplicates are pending. Stops automatically once the tree is clean.</p>
        </div>
        <button onClick={toggleDigest} data-testid="dupe-digest-toggle" role="switch" aria-checked={digest} className={`relative h-6 w-11 rounded-full transition-colors ${digest ? "bg-emerald-500" : "bg-slate-300"}`}>
          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${digest ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>

      <div className="mt-4" data-testid="merge-history">
        <h3 className="mb-2 text-sm font-bold text-slate-800">Merge history</h3>
        {history.length === 0 ? (
          <p className="text-xs text-slate-400" data-testid="merge-history-empty">No merges yet.</p>
        ) : (
          <div className="divide-y divide-slate-50 rounded-xl border border-slate-100">
            {history.map((h, i) => (
              <div key={h.undo_id || i} className="flex flex-wrap items-center gap-2 p-3 text-xs" data-testid={`merge-history-row-${i}`}>
                <span className="font-semibold text-slate-700">{(h.merged || []).join(", ") || "—"}</span>
                <span className="text-slate-300">→</span>
                <span className="text-emerald-700">{(h.into || []).join(", ")}</span>
                {h.undone && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-400" data-testid={`merge-history-undone-${i}`}>UNDONE</span>}
                <span className="ml-auto text-slate-400">{h.created_at ? new Date(h.created_at).toLocaleString() : ""}</span>
                {h.by && <span className="text-slate-400">· {h.by}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </SectionCard>
  );
}

export default function AdminServices() {
  const [tab, setTab] = useState("categories");
  const [counts, setCounts] = useState({ pending: 0, published: 0, total: 0 });
  const [dupeCount, setDupeCount] = useState(0);
  useEffect(() => { api.get("/admin/services?status=all").then((r) => setCounts(r.data.counts || {})).catch(() => {}); }, [tab]);
  useEffect(() => { api.get("/admin/service-categories/dedupe-count").then((r) => setDupeCount(r.data.count || 0)).catch(() => {}); }, [tab]);

  return (
    <div data-testid="admin-services">
      <PageHeader title="Services" icon={Sparkles} subtitle="Manage the services marketplace — category tree, tile images and listing review. Nothing appears to shoppers until approved." />
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <StatCard icon={Layers} label="Pending review" value={counts.pending || 0} tint="amber" testid="stat-pending" />
        <StatCard icon={Check} label="Published" value={counts.published || 0} tint="emerald" testid="stat-published" />
        <StatCard icon={Sparkles} label="Total services" value={counts.total || 0} tint="blue" testid="stat-total" />
      </div>
      <div className="mb-4 flex gap-2">
        <button onClick={() => setTab("categories")} data-testid="tab-categories" className={`rounded-full px-4 py-2 text-sm font-medium ${tab === "categories" ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>Categories</button>
        <button onClick={() => setTab("listings")} data-testid="tab-listings" className={`rounded-full px-4 py-2 text-sm font-medium ${tab === "listings" ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>Listings</button>
        <button onClick={() => setTab("dedupe")} data-testid="tab-dedupe" className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ${tab === "dedupe" ? "brand-gradient-bg text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>Clean up{dupeCount > 0 && <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white" data-testid="dedupe-count-badge">{dupeCount}</span>}</button>
      </div>
      {tab === "categories" ? <CategoriesTab /> : tab === "listings" ? <ListingsTab /> : <DedupeTab />}
    </div>
  );
}
